import { useEffect, useState, useRef, useMemo } from 'react'
import { PlusIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'auth/AuthContext'
import { showAlert } from 'services/alerts.service'
import { makeRequest } from 'services/request.service'
import { getOnChangeFunction, getFormattedMissingFieldsMessage } from 'utils'
import { isLegacyLogoValue } from 'utils/logo.utils'
import ButtonComponent from 'components/common/ButtonComponent'
import LogoUploadComponent from 'components/common/LogoUploadComponent'
import LabeledSelectComponent from 'components/common/LabeledSelectComponent'
import LabeledInputComponent from 'components/common/LabeledInputComponent'
import PageLoadingState from 'components/common/PageLoadingState'

const selectLogoOptions = [
  { value: '1', label: 'One Time' },
  { value: '2', label: 'One Time Shared' },
  { value: '3', label: 'Regular' },
  { value: '4', label: 'Shared' },
  { value: '5', label: 'Units' },
  { value: '6', label: 'Weight (Kg)' }
]

const selectContainerTypeOptions = [
  { value: '1', label: '89 PLASTIC/9.000KG 9.00 PLASTIC' }
]

const selectCreditDisplayOptions = [
  { value: '1', label: 'Units' },
  { value: '2', label: 'Weight (Kg)' }
]

const getSanitizedSettingsPayload = data => {
  const payload = { ...data }

  if (isLegacyLogoValue(payload.logo)) {
    delete payload.logo
  }

  return payload
}

const hasOwn = (object, property) => Object.prototype.hasOwnProperty.call(object, property)

const getUploadedLogoValue = responseData => {
  if (typeof responseData === 'string') return responseData

  return responseData?.logo ||
    responseData?.logoUrl ||
    responseData?.logoPath ||
    responseData?.path ||
    responseData?.url ||
    responseData?.file?.path ||
    responseData?.file?.url
}

const SettingsSection = ({ title, description, badge, children }) => {
  return (
    <section className='border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden'>
      <div className='flex flex-col gap-2 border-b border-gray-200 bg-gray-50 px-5 py-4 sm:flex-row sm:items-start sm:justify-between'>
        <div>
          <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>{badge}</p>
          <h2 className='mt-1 text-lg font-semibold text-greenPrimary'>{title}</h2>
          <p className='mt-1 text-sm text-gray-600'>{description}</p>
        </div>
      </div>
      <div className='p-5'>
        {children}
      </div>
    </section>
  )
}

const FarmDetailsSection = ({ formData, onChangeValue, user, missingFields }) => {
  const { t } = useTranslation()
  return (
    <SettingsSection
      badge={t('mgmt.settings.farm.badge')}
      title={t('mgmt.settings.farm.title')}
      description={t('mgmt.settings.farm.desc')}
    >
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>

        <LabeledInputComponent
          label={t('mgmt.settings.farm.firstName')}
          name='firstName'
          type='text'
          onChange={onChangeValue}
          defaultValue={formData.firstName}
          disabled
        />

        <LabeledInputComponent
          label={t('mgmt.settings.farm.lastName')}
          name='lastName'
          type='text'
          defaultValue={formData.lastName}
          onChange={onChangeValue}
          disabled
        />

        <LabeledInputComponent
          label={t('mgmt.settings.farm.farmName')}
          name='farmName'
          type='text'
          value={formData.farmName}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        />

        <LabeledSelectComponent
          name='timezone'
          label={t('mgmt.settings.farm.timezone')}
          value={formData.timezone}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          {/* {Intl.supportedValuesOf('timeZone').map(tz => (
            <option key={tz} value={tz}>{tz}</option>
          ))} */}
          {[
            'America/Mexico_City',
            'UTC',
            'America/New_York',
            'America/Los_Angeles',
            'Europe/London',
            'Europe/Berlin',
            'Asia/Tokyo',
            'Asia/Shanghai',
            'Asia/Kolkata'
          ].map(tz => (
            <option key={tz} value={tz}>{tz}</option>
          ))}
        </LabeledSelectComponent>

        <LabeledSelectComponent
          name='country'
          label={t('mgmt.settings.farm.country')}
          value={formData.country}
          onChange={onChangeValue}
        >
          <option value='Mexico'>México</option>
          <option value='USA'>Estados Unidos</option>
          <option value='Canada'>Canadá</option>
        </LabeledSelectComponent>

        <LabeledInputComponent
          label={t('mgmt.settings.farm.costBaseHour')}
          name='costBaseHour'
          type='decimal'
          value={formData.costBaseHour}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        />

        <LabeledSelectComponent
          name='currency'
          label={t('mgmt.settings.farm.currency')}
          value={formData.currency}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          {
            ['MXN', 'EUR', 'USD', 'ILS'].map((cr) => {
              return <option key={cr} value={cr}>{cr}</option>
            })
          }
          {/* {
            Intl.supportedValuesOf('currency').map((cr) => {
              return <option key={cr} value={cr}>{cr}</option>
            })
          } */}
        </LabeledSelectComponent>

        {/* <div>
            <label className='block text-sm font-medium text-gray-700' htmlFor='definition-of-director-password'>Change Manage Protect Password *</label>
            <input
              type='password'
              id='definition-of-director-password'
              name='definitionOfDirectorPassword'
              value={formData.definitionOfDirectorPassword}
              onChange={onChangeValue}
              className='w-full mt-1 p-2 border rounded-md shadow-sm'
            />
          </div> */}

        <LabeledInputComponent
          label={t('mgmt.settings.farm.seasonStartDay')}
          name='seasonStartDay'
          type='number'
          value={formData.seasonStartDay}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        />

        <LabeledSelectComponent
          name='seasonStartMonth'
          label={t('mgmt.settings.farm.seasonStartMonth')}
          value={formData.seasonStartMonth}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
            <option key={m} value={String(m)}>{t(`mgmt.settings.months.${m}`)}</option>
          ))}
        </LabeledSelectComponent>

      </div>
    </SettingsSection>
  )
}

const WorkConfigurationSection = ({ formData, onChangeValue, missingFields }) => {
  const { t } = useTranslation()
  return (
    <SettingsSection
      badge={t('mgmt.settings.work.badge')}
      title={t('mgmt.settings.work.title')}
      description={t('mgmt.settings.work.desc')}
    >
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
        <LabeledSelectComponent
          label={t('mgmt.settings.work.stretchingPolicy')}
          name='stretchingOfActivitiesPolicy'
          value={formData.stretchingOfActivitiesPolicy}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          <option value='1'>{t('mgmt.settings.work.opt.forward')}</option>
          <option value='2'>{t('mgmt.settings.work.opt.backwards')}</option>
          <option value='3'>{t('mgmt.settings.work.opt.none')}</option>
        </LabeledSelectComponent>

        <LabeledSelectComponent
          label={t('mgmt.settings.work.workdaySourcePolicy')}
          name='regulationsRelatedToTheWorkingDay'
          value={formData.regulationsRelatedToTheWorkingDay}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          <option value='1'>{t('mgmt.settings.work.opt.methodA')}</option>
          <option value='2'>{t('mgmt.settings.work.opt.methodB')}</option>
        </LabeledSelectComponent>

        <LabeledSelectComponent
          label={t('mgmt.settings.work.roundToDefaultPolicy')}
          name='roundToDefaultPolicy'
          value={formData.roundToDefaultPolicy}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          <option value='1'>{t('mgmt.settings.work.opt.no')}</option>
          <option value='2'>{t('mgmt.settings.work.opt.yes')}</option>
        </LabeledSelectComponent>

        <LabeledSelectComponent
          label={t('mgmt.settings.work.workdayActivityUploadPolicy')}
          name='workdayActivityUploadPolicy'
          value={formData.workdayActivityUploadPolicy}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          <option value='1'>{t('mgmt.settings.work.opt.none')}</option>
          <option value='2'>{t('mgmt.settings.work.opt.startEndOnly')}</option>
        </LabeledSelectComponent>

        <LabeledInputComponent
          label={t('mgmt.settings.work.startTime')}
          name='startTime'
          type='time'
          value={formData.startTime}
          onChange={onChangeValue}
        />

        <LabeledInputComponent
          label={t('mgmt.settings.work.endTime')}
          name='endTime'
          type='time'
          value={formData.endTime}
          onChange={onChangeValue}
        />

        <LabeledInputComponent
          label={t('mgmt.settings.work.breaksCounts')}
          name='numberOfBrakes'
          type='number'
          value={formData.numberOfBrakes}
          onChange={onChangeValue}
        />

        <LabeledInputComponent
          label={t('mgmt.settings.work.breakStart')}
          name='brakeStartTime'
          type='time'
          value={formData.brakeStartTime}
          onChange={onChangeValue}
        />

        <LabeledInputComponent
          label={t('mgmt.settings.work.breakEnd')}
          name='brakeEndTime'
          type='time'
          value={formData.brakeEndTime}
          onChange={onChangeValue}
        />

        <LabeledInputComponent
          label={t('mgmt.settings.work.irregularThreshold')}
          name='irregularThreshold'
          type='text'
          value={formData.irregularThreshold}
          onChange={onChangeValue}
        />

        <LabeledSelectComponent
          name='reuseConfiguration'
          label={t('mgmt.settings.work.reuseConfiguration')}
          value={formData.reuseConfiguration}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          <option value='1'>{t('mgmt.settings.work.opt.regular')}</option>
          <option value='2'>{t('mgmt.settings.work.opt.shared')}</option>
        </LabeledSelectComponent>

      </div>
    </SettingsSection>
  )
}

const LogoConfigurationSection = ({ formData, onChangeValue, onLogoUpload }) => {
  const { t } = useTranslation()
  return (
    <SettingsSection
      badge={t('mgmt.settings.logoSection.badge')}
      title={t('mgmt.settings.logoSection.title')}
      description={t('mgmt.settings.logoSection.desc')}
    >
      <div className='grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,280px)_1fr]'>

        <div>
          <LogoUploadComponent
            logo={formData.logo}
            onUpload={onLogoUpload}
            onReset={() => onChangeValue({ target: { name: 'logo', value: null } })}
          />
        </div>

        <div className='grid grid-cols-1 gap-6 md:grid-cols-3'>
          <LabeledSelectComponent
            name='reuseSetting'
            label={t('mgmt.settings.logoSection.reuseSettings')}
            value={formData.reuseSetting}
            onChange={onChangeValue}
          >
            {selectLogoOptions.map(option => (
              <option key={option.value} value={option.label}>{option.label}</option>
            ))}
          </LabeledSelectComponent>

          <LabeledSelectComponent
            name='containerId'
            label={t('mgmt.settings.logoSection.containerType')}
            value={formData.containerId}
            onChange={onChangeValue}
          >
            {selectContainerTypeOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </LabeledSelectComponent>

          <LabeledSelectComponent
            name='creditDisplayOption'
            label={t('mgmt.settings.logoSection.creditDisplay')}
            value={formData.creditDisplayOption}
            onChange={onChangeValue}
          >
            {selectCreditDisplayOptions.map(option => (
              <option key={option.value} value={option.label}>{option.label}</option>
            ))}
          </LabeledSelectComponent>
        </div>

      </div>
    </SettingsSection>
  )
}

const Settings = () => {
  const hasFetched = useRef(false)
  const { user, setUser } = useAuth()
  const { t } = useTranslation()

  const [state, setState] = useState({ formData: {}, missingFields: [], loading: true })

  const onChangeValue = getOnChangeFunction(setState, state.formData)

  const getGeneralSettings = async () => {
    const response = await makeRequest({
      method: 'get',
      url: `/management/general-settings/${user.userId}`,
      headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
    })

    if (response.status === 200) {
      const formData = getSanitizedSettingsPayload(response.data)

      setState(gs => ({
        ...gs,
        formData,
        loading: false
      }))
      return
    }

    setState(gs => ({ ...gs, loading: false }))
  }

  const createGeneralSettings = async (data) => {
    const result = await makeRequest({
      method: 'post',
      url: `/management/general-settings/${user.userId}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      },
      data,
      alertResult: true
    })

    if (result.status === 200) {
      const nextUser = {
        ...user,
        farmId: result.data.farm.farmId
      }

      if (hasOwn(data, 'logo')) {
        nextUser.logo = data.logo
      }

      setUser(nextUser)
    }
  }

  const uploadLogo = async (file) => {
    const logoFormData = new FormData()
    logoFormData.append('file', file)

    const response = await makeRequest({
      method: 'post',
      url: `/management/general-settings/${user.userId}/logo`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`,
        'Content-Type': 'multipart/form-data'
      },
      data: logoFormData
    })

    const logo = getUploadedLogoValue(response.data)

    if (!logo) {
      throw new Error('Logo upload response did not include a logo path')
    }

    setState(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        logo
      }
    }))

    return logo
  }

  const onSave = async (e) => {
    e.preventDefault()

    const requiredFields = [
      'farmName',
      'timezone',
      'costBaseHour',
      'currency',
      'seasonStartDay',
      'seasonStartMonth',
      'stretchingOfActivitiesPolicy',
      'regulationsRelatedToTheWorkingDay',
      'roundToDefaultPolicy',
      'workdayActivityUploadPolicy',
      'reuseConfiguration'
    ]

    const missingFields = requiredFields.filter(field => {
      const value = state.formData[field]
      return value === undefined || value === null || value === ''
    })

    setState(prev => ({ ...prev, missingFields }))

    if (missingFields.length > 0) {
      const htmlMessage = getFormattedMissingFieldsMessage(state.formData, requiredFields)
      showAlert({
        html: htmlMessage,
        icon: 'warning',
        focus: missingFields[0]
      })

      return
    }

    await createGeneralSettings(getSanitizedSettingsPayload(state.formData))
  }

  useEffect(function () {
    if (!user?.userId) return
    if (hasFetched.current) return
    hasFetched.current = true

    getGeneralSettings()
  }, [user?.userId])

  // const isFieldMissing = fieldName => state.missingFields.includes(fieldName)

  const missingFieldsSet = useMemo(() => new Set(state.missingFields), [state.missingFields])

  if (state.loading) {
    return <PageLoadingState message={t('mgmt.settings.loading')} rows={12} />
  }

  return (
    <form autoComplete='off' className='space-y-5 pb-24'>
      <div className='border border-gray-200 rounded-lg bg-white px-5 py-4 shadow-sm'>
        <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>{t('mgmt.settings.managementBadge')}</p>
        <h1 className='mt-1 text-xl font-semibold text-greenPrimary'>{t('mgmt.settings.title')}</h1>
        <p className='mt-1 text-sm text-gray-600'>
          {t('mgmt.settings.subtitle')}
        </p>
      </div>

      <LogoConfigurationSection formData={state.formData} onChangeValue={onChangeValue} onLogoUpload={uploadLogo} />

      <FarmDetailsSection
        formData={state.formData}
        onChangeValue={onChangeValue}
        user={user}
        missingFields={missingFieldsSet}
      />

      <WorkConfigurationSection
        formData={state.formData}
        onChangeValue={onChangeValue}
        missingFields={missingFieldsSet}
      />

      <div className='sticky bottom-0 -mx-1 bg-white/95 px-1 pt-4 pb-2 flex justify-end border-t border-gray-200 shadow-[0_-8px_20px_rgba(15,23,42,0.06)]'>
        <ButtonComponent onClick={onSave}>
          <PlusIcon className='w-5 h-5 mr-1 text-white' />
          {t('mgmt.settings.save')}
        </ButtonComponent>
      </div>
    </form>
  )
}

export default Settings
